import { forwardRef } from 'preact/compat';
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'preact/hooks';

import { html } from '@shared/utils/rendering/html';

import Select from '@shared/components/forms/form-components/selects';
import Button from '@shared/components/buttons';
import Table from '@shared/components/tables';

import { useCachedFetch } from '@core/utils/hooks/use-cached-fetch';
import {
    UserLegalEntityColumns,
    UserLegalEntityFilters,
} from '@core/configs/admin/users/user-legal-entity-table.config';

const UserLegalEntities = forwardRef(({ inputProps, layoutSettings }, ref) => {
    const selectRef = useRef(null);
    const { data: legalEntities, isLoading, errors, refetch } = useCachedFetch('/legal-entities');

    const [formData, setFormData] = useState(null);
    const [filteredEntities, setFilteredEntities] = useState([]);

    const resolveEntityId = (entity) => {
        if (entity && typeof entity === 'object') {
            return entity.value ?? entity.id ?? null;
        }

        return entity ?? null;
    };

    const setInnerFieldValue = (value, _) => {
        setFormData(value);
    };

    useImperativeHandle(ref, () => ({
        clear: () => {
            // Reset internal component state
            setFormData(null);
            selectRef.current?.clear();
            // Re-filter entities based on the current field value (after form reset)
            filterEntities(inputProps.field.value);
        },
        refetch: () => refetch(),
    }));

    const linkEntity = () => {
        const selectedEntityId = resolveEntityId(formData);
        const value = legalEntities?.find((x) => +x.value === +selectedEntityId);
        if (!value) {
            return;
        }

        const fieldValue = inputProps.field.value ?? [];

        const values = (Array.isArray(fieldValue) ? fieldValue : [fieldValue]).filter(Boolean);

        const hasEntity = values.some(
            (entity) => +resolveEntityId(entity) === +resolveEntityId(value)
        );
        if (hasEntity) {
            selectRef.current?.clear();
            setFormData(null);
            return;
        }

        // Create a new array instead of mutating the original
        const newValues = [...values, value];

        inputProps.field?.setValue(newValues, inputProps.field);
        filterEntities(newValues);
        selectRef.current?.clear();
        setFormData(null);
    };

    // Receives id via Button eventProps — avoids an inline arrow function per re-render
    const unlinkEntity = (event, id) => {
        const fieldValue = inputProps.field.value ?? [];
        const prev = (Array.isArray(fieldValue) ? fieldValue : [fieldValue]).filter(Boolean);
        const next = prev.filter((x) => +resolveEntityId(x) !== +id);

        filterEntities(next);
        inputProps.field?.setValue(next, inputProps.field);
    };

    const rowActions = (row) => {
        const rowId = row.id ?? row.value;
        return html`
            <div class="row-actions">
                <${Button}
                    buttonType="tertiary"
                    label=${{ text: 'Unlink' }}
                    onClick=${unlinkEntity}
                    eventProps=${{ id: rowId }}
                />
            </div>
        `;
    };

    const filterEntities = (data) => {
        const blacklistIds = data?.filter(Boolean).map((x) => +resolveEntityId(x)) ?? [];
        const filtered = legalEntities?.filter((entity) => !blacklistIds.includes(+entity.value));
        setFilteredEntities(filtered);
    };

    useEffect(() => {
        if (legalEntities) {
            setFilteredEntities(legalEntities);
            filterEntities(inputProps.field.value);
        }
    }, [legalEntities]);

    // Watch for changes in the field value (like when form is reset)
    useEffect(() => {
        if (legalEntities) {
            filterEntities(inputProps.field.value);
        }
    }, [inputProps.field.value, legalEntities]);

    const tableData = useMemo(() => {
        const rows = (
            Array.isArray(inputProps?.field?.value)
                ? inputProps.field.value
                : [inputProps?.field?.value]
        ).filter(Boolean);

        const newTableData = {
            total: rows.length,
            rows,
            isLoading,
        };
        return newTableData;
    }, [inputProps?.field?.value, isLoading]);

    return html`
        <div class="row">
            <div class="col-4">
                <div class="form-group">
                    <${Select}
                        ref=${selectRef}
                        label=${{ text: 'Select Legal Entity', include: true }}
                        field=${{
                            name: 'legalEntitySelect',
                            value: formData,
                            setValue: setInnerFieldValue,
                            options: filteredEntities,
                        }}
                    />
                </div>
            </div>

            <div class="col-4">
                <div class="form-group">
                    <${Button}
                        label=${{ text: 'Link' }}
                        disabled=${!formData}
                        onClick=${linkEntity}
                        css="align-form-group"
                    />
                </div>
            </div>
        </div>

        <${Table}
            config=${{
                columns: UserLegalEntityColumns,
                filters: UserLegalEntityFilters,
                actions: rowActions,
            }}
            data=${tableData}
        />
    `;
});

export default UserLegalEntities;
